import { Router } from '@angular/router';
import { HttpHeaders } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable, delay, finalize, map, of, switchMap, take, tap } from 'rxjs'
import { AppStateService } from '../store/state/app/app.state.service'
import { UserStateService } from '../store/state/user/user.state.service'
import { DataService } from './data.service'
import { DeviceService } from './device.service'
import { EnvironmentService } from './environment.service'
import { LoginReq, LoginResp, UserWelcomeResponse } from '../models/Login'
import { DiscoveryResponse, SsoExchangeRequest } from '../models/Discovery'
import { CreatePasswordReq } from '../models/CreatePassword'
import { ForgotPasswordResp, ForgotPasswordReq } from '../models/ForgotPassword'
import { SessionTypeEnum } from '../models/SessionTypeEnum'
import { ValidatePasscodeResp, ValidatePasscodeReq } from '../models/ValidatePasscode'
import * as uuid from 'uuid'
import { Device } from '../models/Device';
import { Session, Token } from '../models/Gateway';
import { AppEventService, EVENT_TYPE } from './app-event.service';
import { LoginFlowStateService } from '../store/state/login-flow/login-flow.state.service';
import { OrderStateServiceWrapper } from '../store/state/order/order.state.wrapper.service';
import { GenericStateService } from '../store/generic-state/generic.state.service';
import { AnalyticsService } from './analytics/analytics.service';
import { NotificationService } from './notification.service';
import { Message } from '../enums/message.enum';
import * as Sentry from "@sentry/angular";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  #logoutInFlight = false
  #environmentService = inject(EnvironmentService)
  #dataService = inject(DataService)
  #deviceService = inject(DeviceService)
  #appStateService = inject(AppStateService)
  #userStateService = inject(UserStateService)
  #loginFlowStateService = inject(LoginFlowStateService)
  orderStateService = inject(OrderStateServiceWrapper)
  #genericStateService = inject(GenericStateService)
  #eventService: AppEventService = inject(AppEventService)
  #notificationService = inject(NotificationService)
  analyticsService = inject(AnalyticsService);

  #router = inject(Router)
  #tokenUrl = this.#environmentService.getUrl('oauth/token')
  #sessionUrl = this.#environmentService.getUrl('ng/v1/common/startSession')
  #loginUrl = this.#environmentService.getUrl('ng/v1/user/login/')
  #discoveryUrl = this.#environmentService.getUrl('ng/v1/user/discovery/')
  #ssoExchangeUrl = this.#environmentService.getUrl('ng/v1/user/sso/exchange/')
  #forgotPasswordUrl = this.#environmentService.getUrl('ng/v1/user/validate/')
  #validatePinUrl = this.#environmentService.getUrl('ng/v1/user/validatepin/')
  #userUpdateUrl = this.#environmentService.getUrl('ng/v1/user/update/')
  #logoutUrl = this.#environmentService.getUrl('ng/v1/user/logout/')
  #userWelcomeUrl = this.#environmentService.getUrl('ng/v1/user/welcome/')
  #updateSessionUrl = this.#environmentService.getUrl('ng/v1/common/updateSession/')
  public async initDevice(skipLocationRequest: boolean = false) {
    return await this.#deviceService.initDevice(skipLocationRequest)
  }

  initAuthInformationAndLogin(value: { email: string; pass: string; rememberMe: boolean, byPassLocation?: boolean }): Observable<UserWelcomeResponse> {
    return this.fetchToken()
      .pipe(
        delay(500),
        switchMap(() => this.fetchSession()),
        tap((resp) => {
          this.#appStateService.setSession(resp);
        }),
        switchMap(() => this.login(value))
      )
  }

  initAuthInformationAndDiscovery(email: string): Observable<DiscoveryResponse> {
    return this.fetchToken()
      .pipe(
        delay(500),
        switchMap(() => this.fetchSession()),
        tap((resp) => {
          this.#appStateService.setSession(resp);
        }),
        switchMap(() => this.discovery(email))
      )
  }

  discovery(email: string): Observable<DiscoveryResponse> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const endpoint = this.#discoveryUrl + sessionId
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#deviceService.getHashForDeviceId(uuid.v4(), email)
      .pipe(switchMap((deviceId) => {
        const payload = { email, deviceId }
        return this.#dataService.post<typeof payload, DiscoveryResponse>(endpoint, payload, reqHeaders, true)
      }))
  }

  fetchToken(): Observable<Token> {
    const headers: HttpHeaders = new HttpHeaders({
      'Authorization': `Basic ${this.#environmentService.env.tokenCredentials}`
    })
    return this.#dataService.get<Token>(this.#tokenUrl, headers, true, 'json').pipe(take(1), tap((token: Token) => {
      if (token.issued_at && token.expires_in) {
        const now = new Date();
        token.startDate = new Date();
        const tokenTime = token.expires_in.toDate().getTime() - token.issued_at.toDate().getTime();
        const endDate = new Date();
        endDate.setTime(now.getTime() + tokenTime);
        token.endDate = endDate;
      }
      this.#appStateService.setToken(token)
    }
    ));
  }

  fetchSession(): Observable<Session> {
    return this.#dataService.get<Session>(this.#sessionUrl)
  }

  updateSession() {
    const sessionId = this.#appStateService.getSession()?.SessionID
    this.#dataService.post<unknown, Session>(this.#updateSessionUrl + sessionId).subscribe({
      next: (resp) => this.#appStateService.setSession(resp),
      error: () => { } // Explicitly swallow error because it will be logged with sentry using http interceptor
    });
  }

  processSAML() {

  }

  login(formData: { email: string; pass: string; rememberMe: boolean, byPassLocation?: boolean }): Observable<UserWelcomeResponse> {
    const sessionId = this.#appStateService.getSession()?.SessionID

    const device = this.#appStateService.getDevice()
    if (!device) {
      return of({} as UserWelcomeResponse)
    }

    return this.#deviceService.getHashForDeviceId(uuid.v4(), formData.email)
      .pipe(switchMap((deviceIdHash) => {
        const payload = this.#buildLoginPayload(formData, device, deviceIdHash)
        const endpoint = this.#loginUrl + sessionId
        const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
        return this.#dataService.post<LoginReq, LoginResp>(endpoint, payload, reqHeaders, true)
      }), switchMap((resp) => {
        const authorized = this.#isAuthorized(resp)
        this.#loginFlowStateService.setLoginFlow(resp.LoginFlow)
        this.#userStateService.setUser(resp.UserDetails)
        this.analyticsService.setUser(sessionId, resp);
        this.setSentryUser(authorized)
        return !authorized ? of(null) : this.getWelcomeInfo()
      }),
        map(val => {
          if (val) {
            this.#eventService.raiseEventOf<UserWelcomeResponse>(EVENT_TYPE.PROFILE, val)
          }
          return val
        }))
  }


  ssoLogin(accessToken: string, email: string, byPassLocation: boolean): Observable<UserWelcomeResponse> {
    const sessionId = this.#appStateService.getSession()?.SessionID

    const device = this.#appStateService.getDevice()
    if (!device) {
      return of({} as UserWelcomeResponse)
    }

    return this.#deviceService.getHashForDeviceId(uuid.v4(), email)
      .pipe(switchMap((deviceIdHash) => {
        const payload = this.#buildSsoPayload(accessToken, device, deviceIdHash, byPassLocation)
        const endpoint = this.#ssoExchangeUrl + sessionId
        const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
        return this.#dataService.post<SsoExchangeRequest, LoginResp>(endpoint, payload, reqHeaders, true)
      }), switchMap((resp) => {
        const authorized = this.#isAuthorized(resp)
        this.#loginFlowStateService.setLoginFlow(resp.LoginFlow)
        this.#userStateService.setUser(resp.UserDetails)
        this.analyticsService.setUser(sessionId, resp);
        this.setSentryUser(authorized)
        return !authorized ? of(null) : this.getWelcomeInfo()
      }),
        map(val => {
          if (val) {
            this.#eventService.raiseEventOf<UserWelcomeResponse>(EVENT_TYPE.PROFILE, val)
          }
          return val
        }))
  }

  getWelcomeInfo(): Observable<UserWelcomeResponse> {
    const sessionId = this.#appStateService.getSession()?.SessionID;
    const endpoint = this.#userWelcomeUrl + sessionId;
    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#dataService.get<UserWelcomeResponse>(endpoint, reqHeaders)
      .pipe(tap((resp) => {
        this.#userStateService.setUserWelcome(resp)
        this.#userStateService.setAuthorized(true)
      }))
  }

  saveBasicInformation(formData: { firstName: string; lastName: string, securityQuestion: number, securityAnswer: string }) {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const userId = this.#userStateService.getUnauthorizedUser()?.UserId

    const payload = {
      UserId: userId,
      FirstName: formData.firstName?.trim(),
      LastName: formData.lastName?.trim(),
      SecurityQuestionId: formData.securityQuestion,
      SecurityQuestionAnswer: formData.securityAnswer,
    }

    const endpoint = this.#userUpdateUrl + sessionId

    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#dataService.put<unknown, unknown>(endpoint, payload, reqHeaders)
  }

  initForgotPassword(formData: { email: string }) {
    return this.fetchToken()
      .pipe(
        tap((token: Token) => {
          this.#appStateService.setToken(token)
        }),
        delay(500),
        switchMap(() => {
          return this.fetchSession()
        }),
        switchMap((resp) => {
          this.#appStateService.setSession(resp)
          return this.forgotPassword(formData)
        })
      )
  }

  forgotPassword(formData: { email: string }): Observable<ForgotPasswordResp> {
    const sessionId = this.#appStateService.getSession()?.SessionID

    const payload: ForgotPasswordReq = {
      Email: formData.email,
      SessionType: SessionTypeEnum.FORGOT_PASSWORD
    }

    const endpoint = this.#forgotPasswordUrl + sessionId

    const reqHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('skip', 'true') // Skip the interceptor to prevent misleading toast
    return this.#dataService.post<ForgotPasswordReq, ForgotPasswordResp>(endpoint, payload, reqHeaders)
  }

  validatePasscode(formData: { pass: number }): Observable<ValidatePasscodeResp> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const userId = this.#userStateService.getUnauthorizedUser()?.UserId

    const payload: ValidatePasscodeReq = {
      UserId: userId,
      Pin: formData.pass,
      SessionType: SessionTypeEnum.FORGOT_PASSWORD
    }

    const endpoint = this.#validatePinUrl + sessionId

    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#dataService.post<ValidatePasscodeReq, ValidatePasscodeResp>(endpoint, payload, reqHeaders)
  }

  createPassword(formData: { pass: string }, SessionType = SessionTypeEnum.FORGOT_PASSWORD): Observable<unknown> {
    const sessionId = this.#appStateService.getSession()?.SessionID
    const userId = this.#userStateService.getUnauthorizedUser()?.UserId

    const payload: CreatePasswordReq = {
      UserId: userId,
      Password: formData.pass,
      SessionType
    }

    const endpoint = this.#userUpdateUrl + sessionId

    const reqHeaders = new HttpHeaders().set('Content-Type', 'application/json')
    return this.#dataService.put<CreatePasswordReq, unknown>(endpoint, payload, reqHeaders)
  }


  logout(sessionExpired = false) {
    if (this.#logoutInFlight) {
      return of(null)
    }
    this.#logoutInFlight = true
    const sessionId = this.#appStateService.getSession()?.SessionID
    if (!sessionId) {
      this.#completeLogout(sessionExpired)
      return of(null)
    }
    const endpoint = this.#logoutUrl + sessionId
    return this.#dataService.post(endpoint, undefined, undefined, true).
      pipe(finalize(() => this.#completeLogout(sessionExpired)));
  }

  // navigation and the toast must live here behind #logoutInFlight, not in the component.
  #completeLogout(sessionExpired: boolean) {
    this.#resetState()
    if (sessionExpired) {
      this.#notificationService.showInfo(Message.SessionExpiredInactivity)
    } else {
      this.#notificationService.showSuccess(Message.LogoutSuccess)
    }
    this.#router.navigate(['/login']).finally(() => { this.#logoutInFlight = false })
  }

  #buildLoginPayload(formData: { email: string; pass: string; rememberMe: boolean, byPassLocation?: boolean }, device: Device, deviceIdHash: string) {
    return {
      Email: formData.email,
      Password: formData.pass,
      DeviceID: deviceIdHash,
      Latitude: device.latitude,
      Longitude: device.longitude,
      BrowserType: device.browserName,
      BrowserPlatform: device.platform,
      BrowserIsMobile: device.browserIsMobile,
      BrowserMobileModel: device.browserMobileModel,
      BrowserUserAgent: device.userAgent,
      GeoAccuracy: device.geoAccuracy,
      LocationServicesOptional: formData.byPassLocation ? true : false
    }
  }

  #buildSsoPayload(accessToken: string, device: Device, deviceIdHash: string, byPassLocation: boolean): SsoExchangeRequest {
    return {
      AccessToken: accessToken,
      DeviceID: deviceIdHash,
      Latitude: device.latitude,
      Longitude: device.longitude,
      BrowserType: device.browserName,
      BrowserPlatform: device.platform,
      BrowserIsMobile: device.browserIsMobile,
      BrowserMobileModel: device.browserMobileModel,
      BrowserUserAgent: device.userAgent,
      GeoAccuracy: device.geoAccuracy,
      AppVersion: this.#environmentService.env.appVersion,
      LocationServicesOptional: byPassLocation ? true : false
    }
  }

  #resetState() {
    this.#userStateService.reset();
    this.orderStateService.reset();
    this.#genericStateService.reset();
    sessionStorage.clear();
  }

  #isAuthorized(resp: LoginResp): boolean {
    return !(resp.LoginFlow.Do2FaCheck || resp.LoginFlow.Do2FaSetup || resp.LoginFlow.DoForcedPasswordReset || resp.LoginFlow.DoSecurityQandA)
  }

  refreshSession() {
    // Always fetch token but update session only if expiring within 30 minutes
    this.fetchToken().pipe(take(1)).subscribe(() => {
      const session: Session = this.#appStateService.getSession();
      if (!session?.EndDateTime) {
        return;
      }

      const endTimeUtc = new Date(session.EndDateTime);
      const diffInMinutes = (endTimeUtc.getTime() - Date.now()) / (1000 * 60);

      // Update only if session is still valid and expiring soon
      if (diffInMinutes > 0 && diffInMinutes <= 30) {
        this.updateSession();
      }
    });
  }

  setSentryUser(authorized: boolean) {
    if (authorized) {
      Sentry.setUser({
        id: this.#userStateService.getUser()?.UserId, email: this.#userStateService.getUser()?.LoginEmail,
        username: this.#userStateService.getUser()?.FirstName + ' ' + this.#userStateService.getUser()?.LastName
      })
    } else {
      Sentry.setUser({ id: this.#userStateService.getUnauthorizedUser()?.UserId, email: this.#userStateService.getUnauthorizedUser()?.Email })
    }
  }

}
